import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Users, CheckCircle2, Clock, AlertCircle, Wallet, PieChart } from "lucide-react";
import StatCard from "../components/StatCard";
import StatusBadge from "../components/StatusBadge";
import Select from "../components/Select";
import { useData } from "../context/DataContext";
import {
  formatKES,
  computeTenantStatus,
  tenantDueDay,
  paidForMonth,
  tenantBalance,
  currentMonth,
  formatMonth,
  monthOptions,
} from "../utils/format";

export default function Dashboard() {
  const { properties, tenants, payments } = useData();
  const [month, setMonth] = useState(currentMonth());
  const [propertyFilter, setPropertyFilter] = useState("all");

  const propertyOptions = [
    { value: "all", label: "All properties" },
    ...properties.map((p) => ({ value: p.id, label: p.name })),
  ];

  const active = useMemo(() => {
    return tenants
      .filter((t) => t.status === "ACTIVE")
      .filter((t) => (propertyFilter === "all" ? true : t.propertyId === propertyFilter));
  }, [tenants, propertyFilter]);

  const counts = useMemo(() => {
    const statuses = active.map((t) => computeTenantStatus(t, payments, month));
    return {
      paid: statuses.filter((s) => s === "PAID").length,
      partial: statuses.filter((s) => s === "PARTIAL").length,
      pending: statuses.filter((s) => s === "PENDING").length,
      overdue: statuses.filter((s) => s === "OVERDUE").length,
    };
  }, [active, payments, month]);

  const expectedMonthly = active.reduce((a, t) => a + (Number(t.rent) || 0), 0);
  const collectedMonthly = active.reduce(
    (a, t) => a + Math.min(Number(t.rent) || 0, paidForMonth(payments, t.id, month)),
    0
  );
  const pct = expectedMonthly > 0 ? Math.round((collectedMonthly / expectedMonthly) * 100) : 0;

  const recent = active.slice(0, 5);

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold sm:text-2xl text-gray-900 dark:text-white">{(() => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"; })()}</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Here's an overview of your portfolio.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            className="w-40"
            value={propertyFilter}
            onChange={setPropertyFilter}
            options={propertyOptions}
          />
          <Select
            className="w-40"
            value={month}
            onChange={setMonth}
            options={monthOptions()}
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Properties" value={properties.length} icon={Building2} hint="Total properties" />
        <StatCard label="Tenants" value={active.length} icon={Users} hint={`${tenants.filter((t) => t.status === "VACATED").length} vacated`} />
        <StatCard label="Paid" value={counts.paid} icon={CheckCircle2} hint={formatMonth(month)} tone="success" />
        <StatCard
          label="Outstanding"
          value={counts.pending + counts.overdue + counts.partial}
          icon={AlertCircle}
          hint={`${counts.overdue} overdue · ${counts.partial} partial · ${counts.pending} pending`}
          tone={counts.overdue > 0 ? "danger" : "warning"}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="card p-5 lg:col-span-2">
          <div className="mb-4 flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
              <Wallet className="h-4 w-4" />
            </span>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Monthly collection · {formatMonth(month)}
            </h3>
          </div>
          <div className="flex items-baseline justify-between">
            <div>
              <div className="text-2xl font-semibold text-gray-900 dark:text-white">
                {formatKES(collectedMonthly)}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                of {formatKES(expectedMonthly)} expected
              </div>
            </div>
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {pct}% collected
            </div>
          </div>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
            <div
              className="h-full bg-indigo-600 transition-all duration-200"
              style={{ width: `${Math.min(100, pct)}%` }}
            />
          </div>
        </div>

        <div className="card p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Status breakdown · {formatMonth(month)}
          </h3>
          <ul className="mt-4 space-y-3">
            <Row icon={CheckCircle2} tone="text-green-600 bg-green-50" label="Paid" value={counts.paid} />
            <Row icon={PieChart} tone="text-blue-600 bg-blue-50" label="Partial" value={counts.partial} />
            <Row icon={Clock} tone="text-amber-600 bg-amber-50" label="Pending" value={counts.pending} />
            <Row icon={AlertCircle} tone="text-red-600 bg-red-50" label="Overdue" value={counts.overdue} />
          </ul>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 dark:border-gray-700 px-5 py-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Recent tenants</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">Latest active tenants</p>
          </div>
          <Link to="/app/tenants" className="text-sm font-medium text-indigo-600 hover:text-indigo-700">
            View all →
          </Link>
        </div>
        {recent.length === 0 ? (
          <div className="px-5 py-10 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">No tenants yet.</p>
            <Link to="/app/tenants" className="mt-3 inline-block text-sm font-medium text-indigo-600 hover:text-indigo-700">
              Add your first tenant →
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <Th>Tenant</Th>
                  <Th>House</Th>
                  <Th>Rent</Th>
                  <Th>Due day</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
                {recent.map((t) => {
                  const property = properties.find((p) => p.id === t.propertyId);
                  const status = computeTenantStatus(t, payments, month);
                  const balance = tenantBalance(t, payments, month);
                  return (
                    <tr key={t.id}>
                      <Td className="font-medium text-gray-900 dark:text-white">
                        {t.name}
                        <div className="text-xs font-normal text-gray-500 dark:text-gray-400">
                          {property?.name || "—"}
                        </div>
                      </Td>
                      <Td>{t.houseNumber}</Td>
                      <Td>{formatKES(t.rent)}</Td>
                      <Td>{ordinal(tenantDueDay(t))} of month</Td>
                      <Td>
                        <StatusBadge status={status.toLowerCase()} />
                        {balance > 0 && (
                          <div className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                            {formatKES(balance)} due
                          </div>
                        )}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ordinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function Row({ icon: Icon, tone, label, value }) {
  return (
    <li className="flex items-center justify-between">
      <div className="flex items-center gap-2.5">
        <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${tone}`}>
          <Icon className="h-4 w-4" />
        </span>
        <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
      </div>
      <span className="text-sm font-semibold text-gray-900 dark:text-white">{value}</span>
    </li>
  );
}
function Th({ children }) {
  return (
    <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{children}</th>
  );
}
function Td({ children, className = "" }) {
  return (
    <td className={`whitespace-nowrap px-5 py-3 text-sm text-gray-700 dark:text-gray-300 ${className}`}>{children}</td>
  );
}
